import FeatherIcon from "feather-icons-react";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState, useCallback } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Modal,
  Col,
  Row,
  Spinner,
  Input,
} from "reactstrap";
import api from "../helpers/api";
import { formatCreatedDate } from "../constants/utils";
import PermissionGuard from "../components/Common/PermissionGuard";
import usePermission from "../Hooks/usePermission";
import { useTranslation } from "react-i18next";
import SparePartTypeModal from "../components/SparePartTypeModal";

const SparePartTypes = () => {
  const { t } = useTranslation();

  const currentUser = JSON.parse(localStorage.getItem('authUser'));
  const organizationId = currentUser?.activeOrganizationId ?? null;
  const { hasPermission } = usePermission();

  const [sparePartTypeModal, setSparePartTypeModal] = useState(false);
  const [selectedSparePartType, setSelectedSparePartType] = useState(null);
  const [sparePartTypes, setSparePartTypes] = useState([]);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sparePartTypeDetailsModal, setSparePartTypeDetailsModal] = useState(false);
  const [selectedSparePartTypeForDetails, setSelectedSparePartTypeForDetails] = useState(null);

  const toggleSparePartTypeDetailsModal = (sparePartType = null) => {
    setSparePartTypeDetailsModal(!sparePartTypeDetailsModal);
    setSelectedSparePartTypeForDetails(sparePartType);
  };

  // Toggle spare part type modal for create/edit
  function toggleSparePartTypeModal(sparePartType = null) {
    setSparePartTypeModal(!sparePartTypeModal);
    setSelectedSparePartType(sparePartType || {});
    setErrors({});
    setApiError("");
  }

  const handleSparePartTypeModalClose = () => {
    setSparePartTypeModal(false);
    setSelectedSparePartType(null);
  };

  const handleSparePartTypeUpdate = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setApiError("");
    setErrors({});

    // Validation
    const newErrors = {};
    if (!selectedSparePartType?.name?.trim()) {
      newErrors.name = t('shared.spare_part_type_name_required', 'Spare part type name is required');
    } else if (selectedSparePartType.name.length > 100) {
      newErrors.name = t('shared.spare_part_type_name_max_length', 'Spare part type name must be 100 characters or less');
    }
    if (selectedSparePartType?.description && selectedSparePartType.description.length > 500) {
      newErrors.description = t('shared.description_max_length', 'Description must be 500 characters or less');
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setIsSubmitting(false);
      return;
    }

    try {
      const sparePartTypeData = {
        name: selectedSparePartType.name.trim(),
        description: selectedSparePartType.description?.trim() || "",
        organizationId: organizationId,
      };

      if (selectedSparePartType?.id) {
        const updateData = {
          id: selectedSparePartType.id,
          ...sparePartTypeData
        };
        await api.put("organization-spare-part-type/update", updateData);
      } else {
        await api.post("organization-spare-part-type/create", sparePartTypeData);
      }

      handleSparePartTypeModalClose();
      reloadSparePartTypes(searchTerm);
    } catch (error) {
      console.error("Error during spare part type submission:", error);
      setApiError(error?.response?.data?.message || t('shared.error_submission', 'An error occurred during submission'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      if (selectedSparePartType?.id) {
        await api.delete(`organization-spare-part-type/delete?id=${selectedSparePartType.id}`);
        reloadSparePartTypes(searchTerm);
        setDeleteModal(false);
      }
    } catch (error) {
      console.error("Error deleting spare part type:", error);
      setApiError(error?.response?.data?.message || t('shared.failed_to_delete_spare_part_type', 'Failed to delete spare part type'));
    }
  };

  const handleSearch = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
  };

  const reloadSparePartTypes = useCallback(async (search = "") => {
    if (!organizationId) return;

    setLoading(true);
    try {
      const { data } = await api.get(
        `organization-spare-part-type/getAll?organizationId=${organizationId}&search=${search}`
      );
      setSparePartTypes(data.response || []);
    } catch (error) {
      console.error("Error reloading spare part types:", error);
      setApiError(t('shared.failed_to_load_spare_part_types', 'Failed to load spare part types'));
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  // Debounced search (also handles initial load)
  useEffect(() => {
    const timer = setTimeout(() => {
      reloadSparePartTypes(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, reloadSparePartTypes]);

  return (
    <div className="mt-4">
      <div className="mb-4 d-flex align-items-center gap-3">
        <motion.div
          className="mb-4 d-flex align-items-center gap-3"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <PermissionGuard permission="spare-part-type:create">
            <button
              type="button"
              className="btn btn-soft-success waves-effect waves-light d-flex align-items-center gap-2 transition-all hover-shake"
              onClick={() => {
                toggleSparePartTypeModal();
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                height={21}
                width={21}
              >
                <path fillRule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clipRule="evenodd" />
              </svg>
              {t('shared.add_spare_part_type', 'Add Spare Part Type')}
            </button>
          </PermissionGuard>
          <div
            className="ms-auto"
            style={{ minWidth: "400px", flexGrow: 1, maxWidth: "500px" }}
          >
            <Input
              type="text"
              placeholder={t('shared.search_spare_part_types', 'Search Spare Part Types...')}
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
        </motion.div>
      </div>

      <Card>
        <CardHeader className="d-flex justify-content-between align-items-center">
          <h3 className="card-title text-success">{t('shared.spare_part_types', 'Spare Part Types')}</h3>
        </CardHeader>
        <CardBody>
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th>{t('shared.name', 'Name')}</th>
                  <th>{t('shared.created_date', 'Created Date')}</th>
                  <th style={{width: '120px'}}>{t('shared.actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                <AnimatePresence>
                  {sparePartTypes?.map((sparePartType, ind) => (
                    <motion.tr
                      key={sparePartType.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3 }}
                    >
                      <th scope="row">{ind + 1}</th>
                      <td>
                        <span
                          className="text-primary cursor-pointer hover-underline"
                          onClick={() => toggleSparePartTypeDetailsModal(sparePartType)}
                          style={{ cursor: 'pointer' }}
                        >
                          {sparePartType.name}
                        </span>
                      </td>
                      <td>{formatCreatedDate(sparePartType.createdAt) || '-'}</td>
                      <td>
                        <div className="d-flex gap-3">
                          <PermissionGuard permission="spare-part-type:update">
                            <span
                              className='text-warning'
                              role='button'
                              onClick={() => toggleSparePartTypeModal(sparePartType)}
                            >
                              <FeatherIcon icon="edit" />
                            </span>
                          </PermissionGuard>
                          <PermissionGuard permission="spare-part-type:delete">
                            <span
                              className='text-danger'
                              role='button'
                              onClick={() => {
                                setSelectedSparePartType(sparePartType);
                                setApiError("");
                                setDeleteModal(true);
                              }}
                            >
                              <FeatherIcon icon="trash-2" />
                            </span>
                          </PermissionGuard>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>

          {loading && (
            <div className="text-center py-3">
              <Spinner color="primary" />
            </div>
          )}

          {sparePartTypes.length === 0 && !loading && (
            <div className="text-center py-5 text-muted">
              {searchTerm
                ? t('shared.no_spare_part_types_match', 'No spare part types match your search')
                : t('shared.no_spare_part_types_found', 'No spare part types found')}
            </div>
          )}
        </CardBody>
      </Card>

      {/* Spare Part Type Create/Edit Modal */}
      <Modal
        size="lg"
        isOpen={sparePartTypeModal}
        toggle={() => {
          toggleSparePartTypeModal();
        }}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <div className="modal-header">
            <h5 className="modal-title">
              {selectedSparePartType?.id ? t('shared.edit_spare_part_type', 'Edit Spare Part Type') : t('shared.add_spare_part_type', 'Add Spare Part Type')}
            </h5>
            <button
              onClick={handleSparePartTypeModalClose}
              type="button"
              className="btn-close"
              data-dismiss="modal"
              aria-label={t("shared.close", "Close")}
            ></button>
          </div>
          <div className="modal-body">
            <form onSubmit={handleSparePartTypeUpdate}>
              <Row className="mb-4">
                <label
                  htmlFor="spare-part-type-name-input"
                  className="col-sm-3 col-form-label"
                >
                  {t('shared.name', 'Name')} <span className="text-danger">*</span>
                </label>
                <Col sm={9}>
                  <input
                    type="text"
                    value={selectedSparePartType?.name || ""}
                    onChange={(e) =>
                      setSelectedSparePartType({
                        ...selectedSparePartType,
                        name: e.target.value,
                      })
                    }
                    className={`form-control ${
                      errors.name ? "is-invalid" : ""
                    }`}
                    placeholder={t('shared.enter_spare_part_type_name', 'Enter spare part type name')}
                    id="spare-part-type-name-input"
                    maxLength={100}
                  />
                  {errors.name && (
                    <small className="text-danger">{errors.name}</small>
                  )}
                </Col>
              </Row>

              <Row className="mb-4">
                <label
                  htmlFor="spare-part-type-description-input"
                  className="col-sm-3 col-form-label"
                >
                  {t('shared.description', 'Description')}
                </label>
                <Col sm={9}>
                  <textarea
                    value={selectedSparePartType?.description || ""}
                    onChange={(e) =>
                      setSelectedSparePartType({
                        ...selectedSparePartType,
                        description: e.target.value,
                      })
                    }
                    className={`form-control ${
                      errors.description ? "is-invalid" : ""
                    }`}
                    placeholder={t('shared.enter_spare_part_type_description', 'Enter spare part type description')}
                    id="spare-part-type-description-input"
                    rows={3}
                    maxLength={500}
                  />
                  {errors.description && (
                    <small className="text-danger">{errors.description}</small>
                  )}
                  <small className="text-muted">
                    {selectedSparePartType?.description?.length || 0}/500 {t('shared.characters', 'characters')}
                  </small>
                </Col>
              </Row>

              <div className="mt-4">
                {apiError && (
                  <div className="alert alert-danger">{apiError}</div>
                )}
                <button
                  type="submit"
                  className="btn btn-success w-lg transition-all hover-grow pulse-on-hover"
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? t('shared.processing', 'Processing...')
                    : selectedSparePartType?.id
                    ? t('shared.update', 'Update')
                    : t('shared.add', 'Add')}
                </button>
              </div>
            </form>
          </div>
        </motion.div>
      </Modal>

      {/* Spare Part Type Details Modal */}
      <SparePartTypeModal
        isOpen={sparePartTypeDetailsModal}
        toggle={toggleSparePartTypeDetailsModal}
        sparePartTypeData={selectedSparePartTypeForDetails}
        loading={false}
        onEditSparePartType={hasPermission('spare-part-type:update') ? (sparePartType) => {
          toggleSparePartTypeDetailsModal();
          toggleSparePartTypeModal(sparePartType);
        } : null}
      />

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)}>
        <div className="modal-header">
          <h5 className="modal-title">{t('shared.confirm_delete', 'Confirm Delete')}</h5>
          <button
            type="button"
            className="btn-close"
            onClick={() => setDeleteModal(false)}
            aria-label={t("shared.close", "Close")}
          ></button>
        </div>
        <div className="modal-body">
          <p>
            {t('shared.delete_spare_part_type_confirmation', 'Are you sure you want to delete the spare part type "{{name}}"? This action cannot be undone.', { name: selectedSparePartType?.name })}
          </p>
          {apiError && (
            <div className="alert alert-danger">{apiError}</div>
          )}
        </div>
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setDeleteModal(false)}
          >
            {t('shared.cancel', 'Cancel')}
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={handleDelete}
          >
            {t('shared.delete', 'Delete')}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default SparePartTypes;
